import React, { useState } from 'react';
import type { PreviewRow, RowAction } from '../types';
import { isActionableRow } from '../utils/syncHelpers';

const ACTION_STYLES: Record<RowAction, string> = {
  CREATE:    'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  UPDATE:    'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  LINK:      'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  AMBIGUOUS: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  ERROR:     'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  SKIPPED:   'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
};

const ActionBadge: React.FC<{ action: RowAction }> = ({ action }) => (
  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${ACTION_STYLES[action]}`}>
    {action}
  </span>
);

type FilterAction = RowAction | 'ALL';
const FILTER_OPTIONS: FilterAction[] = ['ALL', 'CREATE', 'UPDATE', 'LINK', 'AMBIGUOUS', 'ERROR', 'SKIPPED'];

interface PreviewRowsTableProps {
  rows: PreviewRow[];
  selected: Set<number>;
  onToggleRow: (rowNumber: number) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
}

const PreviewRowsTable: React.FC<PreviewRowsTableProps> = ({
  rows, selected, onToggleRow, onSelectAll, onClearAll,
}) => {
  const [filter, setFilter] = useState<FilterAction>('ALL');

  const visible = filter === 'ALL' ? rows : rows.filter((r) => r.action === filter);
  const actionableCount = rows.filter((r) => isActionableRow(r.action)).length;

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
        <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
          Preview rows ({rows.length})
        </h3>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onSelectAll} className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline">
            Select all ({actionableCount})
          </button>
          <span className="text-gray-300 dark:text-gray-600">·</span>
          <button type="button" onClick={onClearAll} className="text-xs font-medium text-gray-500 dark:text-gray-400 hover:underline">
            Clear all
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-1 px-5 py-2 border-b border-gray-100 dark:border-gray-800">
        {FILTER_OPTIONS.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => setFilter(opt)}
            className={`px-2.5 py-1 rounded text-xs font-semibold transition-colors ${
              filter === opt
                ? 'bg-blue-600 text-white'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            {opt === 'ALL' ? `All (${rows.length})` : opt}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="w-10 px-4 py-3" />
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Row</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Action</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Name</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Phone</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Doc ID</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Reason</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {visible.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                  No rows match this filter.
                </td>
              </tr>
            )}
            {visible.map((row) => {
              const actionable = isActionableRow(row.action);
              const checked = selected.has(row.rowNumber);
              const name = row.proposedChanges?.name ?? row.currentData?.name;
              const phone = row.proposedChanges?.phone ?? row.currentData?.phone;
              return (
                <tr
                  key={row.rowNumber}
                  className={`transition-colors ${actionable ? 'hover:bg-gray-50 dark:hover:bg-gray-800/50' : 'opacity-60'}`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={!actionable}
                      onChange={() => onToggleRow(row.rowNumber)}
                      className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-400 disabled:cursor-not-allowed"
                    />
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    #{row.rowNumber}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <ActionBadge action={row.action} />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-800 dark:text-gray-200 whitespace-nowrap">
                    {name ?? <span className="text-gray-400 italic">—</span>}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {phone ?? <span className="text-gray-400 italic">—</span>}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    {row.docId ?? <span className="italic">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 max-w-xs truncate">
                    {row.reason}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PreviewRowsTable;
